import { useMemo } from "react";
import { Button, Flex, Grid, ModalV2, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { TaxDataSourceAssignmentStatus } from "~/graphql/supergraphEnums";
import {
  useGetPositionsForCaseQuery,
  useGetTaxDataSourceAssignmentsQuery,
} from "~/graphql/supergraphHooks";
import { toDbId } from "~/utils/graphql-helpers";
import { DOCUMENT_TYPE_MAPPING } from "../../constants";
import { POSITION_CASE_TYPES } from "../FatcaAssignmentModal/constants";
import {
  columnDefinition,
  crsColumnDefinition,
  crsGridOptions,
  defaultColDef,
  type FatcaPositionRow,
  gridOptions,
} from "../FatcaAssignmentModal/gridConfig";
import * as styles from "../FatcaAssignmentModal/styles_vanilla.css";
import { useCrsGridSelection } from "../FatcaAssignmentModal/useCrsGridSelection";
import {
  buildRowData,
  getCurrentCasePositionGlobalId,
  getIsCurrentCaseInvestorOnboarding,
} from "../FatcaAssignmentModal/utils";

interface UnassignmentModalProps {
  profileId: number;
  profileName: string;
  documentType: string;
  documentId: number;
  caseId: number;
  onClose: () => void;
  onUnassign: (positionIds: string[]) => void;
  isUnassigning?: boolean;
}

const UnassignmentModal = ({
  profileId,
  profileName,
  documentType,
  documentId,
  caseId,
  onClose,
  onUnassign,
  isUnassigning = false,
}: UnassignmentModalProps) => {
  const { data: assignmentsData, loading: assignmentsLoading } =
    useGetTaxDataSourceAssignmentsQuery({
      variables: { input: { documentId } },
    });

  const { data: positionsData, loading: positionsLoading } = useGetPositionsForCaseQuery({
    variables: { profileId, caseTypes: [...POSITION_CASE_TYPES] },
    fetchPolicy: "network-only",
  });

  const loading = assignmentsLoading || positionsLoading;

  const positionIdByOnboardingCaseId = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of positionsData?.complianceProfile?.positions ?? []) {
      if (p.onboardingCase) {
        map.set(p.onboardingCase.id, String(toDbId(p.id)));
      }
    }
    return map;
  }, [positionsData]);

  const assignedAssignments = useMemo(
    () =>
      (assignmentsData?.taxDataSourceAssignments ?? []).filter(
        (a) => a.status === TaxDataSourceAssignmentStatus.Assigned,
      ),
    [assignmentsData],
  );

  const referencedCaseIds = useMemo(
    () => new Set(assignedAssignments.map((a) => a.referencedCaseId)),
    [assignedAssignments],
  );

  const rowData = useMemo(() => {
    const allPositions = positionsData?.complianceProfile?.positions ?? [];
    const assignedPositions = allPositions.filter(
      (p) => p.onboardingCase && referencedCaseIds.has(p.onboardingCase.id),
    );
    const allCases = positionsData?.complianceProfile?.complianceCases ?? [];
    return buildRowData(assignedPositions, allCases, profileId, caseId, {
      synthesizeCurrentCase: referencedCaseIds.has(caseId),
    });
  }, [referencedCaseIds, positionsData, profileId, caseId]);

  // A non-IO case's position (e.g. open Remediation) is map-linked to the case but may
  // not yet be surfaced through profile positions, so resolve it from the case itself.
  // IO cases are excluded: their open position is not map-linked and the backend
  // self-references them instead.
  const currentCaseFallbackPositionId = useMemo(() => {
    const cases = positionsData?.complianceProfile?.complianceCases ?? [];
    if (getIsCurrentCaseInvestorOnboarding(cases, caseId)) return undefined;
    const globalId = getCurrentCasePositionGlobalId(cases, caseId);
    return globalId ? String(toDbId(globalId)) : undefined;
  }, [positionsData, caseId]);

  const assignedPositionIds = useMemo(() => {
    const ids = assignedAssignments
      .map(
        (a) =>
          positionIdByOnboardingCaseId.get(a.referencedCaseId) ??
          (a.referencedCaseId === caseId ? currentCaseFallbackPositionId : undefined),
      )
      .filter((id): id is string => Boolean(id));
    return [...new Set(ids)];
  }, [
    assignedAssignments,
    positionIdByOnboardingCaseId,
    caseId,
    currentCaseFallbackPositionId,
  ]);

  const isFatca = documentType === DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue;
  const isCrs = documentType === DOCUMENT_TYPE_MAPPING.selfCertification.enumValue;
  const assignmentLabel = isFatca ? "FATCA" : "CRS";

  const {
    selectedPositionIds,
    selectedRowCount,
    handleSelectionChanged,
    handleRowDataUpdated,
    getRowClass,
  } = useCrsGridSelection();

  const handleUnassign = () => {
    if (isCrs) {
      onUnassign(selectedPositionIds);
    } else {
      onUnassign(assignedPositionIds);
    }
  };

  return (
    <ModalV2
      data-testid="unassignment-modal"
      show={true}
      onHide={onClose}
      title={`Unassign as ${assignmentLabel} source document`}
      size="large"
      closeOnClickOutside={false}
    >
      <ModalV2.Body>
        <Text className={styles.description}>
          This document will be unassigned as the {assignmentLabel} source document for this
          profile. This action will change the {assignmentLabel} source document to &apos;Not
          assigned&apos; for all positions associated with this profile.
        </Text>
        <Flex className={styles.contextContainer} gap={Sizes.XL}>
          <Flex flexDirection="column" gap={Sizes.XS} className={styles.contextColumn}>
            <Text variant="secondary-md">Profile</Text>
            <Text variant="regular-md">{profileName}</Text>
          </Flex>
          <Flex flexDirection="column" gap={Sizes.XS} className={styles.contextColumn}>
            <Text variant="secondary-md">New {assignmentLabel} data source</Text>
            <Text variant="regular-md">--</Text>
          </Flex>
        </Flex>
        <div className={styles.gridWrapper}>
          <Grid<FatcaPositionRow>
            gridOptions={{
              ...(isCrs ? crsGridOptions : gridOptions),
              ...(isCrs
                ? {
                    onSelectionChanged: handleSelectionChanged,
                    onRowDataUpdated: handleRowDataUpdated,
                    getRowClass,
                  }
                : {}),
            }}
            defaultColDef={defaultColDef}
            columnDefs={isCrs ? crsColumnDefinition : columnDefinition}
            rowData={loading ? [] : rowData}
          />
        </div>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex gap={Sizes.MD} justifyContent="flex-end">
          <Button onClick={onClose} variant="neutral" disabled={isUnassigning}>
            Cancel
          </Button>
          <Button
            data-testid="unassign-button"
            variant="danger"
            onClick={handleUnassign}
            disabled={loading || isUnassigning || (isCrs && selectedRowCount === 0)}
          >
            {isCrs ? "Unassign from position" : "Unassign from profile"}
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default UnassignmentModal;
